import { validationLib } from "@agentcall/core/lib"
import { roomType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { type Resolver, useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

export const roomFormSchema = z.object({
  name: roomType.nameZ
    .min(1, "部屋番号を入力してください")
    .regex(validationLib.ALPHANUMERIC_REGEX, validationLib.ALPHANUMERIC_ERROR_MESSAGE),
  layout: roomType.layoutZ,
  size: z
    .string()
    .optional()
    .transform((val) => {
      if (!val || val === "") return undefined
      const num = Number(val)
      return Number.isNaN(num) ? undefined : num
    }),
  floor: z
    .string()
    .optional()
    .transform((val) => {
      if (!val || val === "") return undefined
      const num = Number(val)
      return Number.isNaN(num) ? undefined : num
    }),
})

type RoomFormFormValues = z.input<typeof roomFormSchema>
export type RoomFormValues = z.output<typeof roomFormSchema>

interface RoomFormProps {
  defaultValues?: Partial<RoomFormFormValues>
  onSubmit: (values: RoomFormValues) => Promise<void>
  onCancel: () => void
  isEdit?: boolean
  isSubmitting: boolean
}

export function RoomForm({
  defaultValues,
  onSubmit,
  onCancel,
  isEdit = false,
  isSubmitting,
}: RoomFormProps) {
  const form = useForm<RoomFormFormValues>({
    resolver: zodResolver(roomFormSchema) as Resolver<RoomFormFormValues>,
    defaultValues: {
      name: defaultValues?.name ?? "",
      layout: defaultValues?.layout ?? "",
      size: defaultValues?.size ?? "",
      floor: defaultValues?.floor ?? "",
    },
  })

  useEffect(() => {
    if (defaultValues) {
      form.reset({
        name: defaultValues.name ?? "",
        layout: defaultValues.layout ?? "",
        size: defaultValues.size ?? "",
        floor: defaultValues.floor ?? "",
      } as RoomFormFormValues)
    }
  }, [defaultValues, form])

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await onSubmit(values as RoomFormValues)
        })}
        className="space-y-4"
      >
        <Card className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
          <CardHeader className="border-b border-slate-200 py-2.5 px-5">
            <h2 className="text-sm font-semibold text-[#3f4652]">部屋概要</h2>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-semibold text-[#3f4652]">
                    部屋番号
                    <span className="ml-1 px-1 py-0.5 bg-[#ff3232] rounded-[3px] text-xs font-semibold text-white">
                      必須
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="101" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-x-4">
              <FormField
                control={form.control}
                name="layout"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">間取り</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="1LDK" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="size"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">面積</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-1.5">
                        <Input
                          placeholder="25"
                          type="number"
                          step="0.01"
                          {...field}
                          className="flex-1"
                        />
                        <span className="text-xs font-semibold text-[#3f4652]">㎡</span>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="floor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold text-[#3f4652]">階数</FormLabel>
                    <FormControl>
                      <Input placeholder="1" type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            キャンセル
          </Button>
          <Button type="submit" variant="default" className="min-w-[100px]" disabled={isSubmitting}>
            {isSubmitting ? (isEdit ? "更新中..." : "登録中...") : isEdit ? "更新する" : "登録する"}
          </Button>
        </div>
      </form>
    </Form>
  )
}
